import React, { useState, useEffect, useCallback } from 'react';

const STORAGE_KEY = 'rentalyzer_site_preview_unlock_v2026';

function isPreviewModeEnabled() {
  const v = import.meta.env.VITE_SITE_PREVIEW_MODE;
  if (v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  return (
    s === 'true'
    || s === '1'
    || s === 'yes'
    || s === 'on'
    || s === 'enabled'
  );
}

function getValidPasswords() {
  const raw = import.meta.env.VITE_PREVIEW_ACCESS_PASSWORD || '';
  return raw
    .split(',')
    .map((p) => p.trim())
    .filter(Boolean);
}

function isUnlocked() {
  try {
    return sessionStorage.getItem(STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function setUnlocked() {
  try {
    sessionStorage.setItem(STORAGE_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * When VITE_SITE_PREVIEW_MODE is on and a preview password is set at build time,
 * show a coming-soon screen until the user enters a valid password (sessionStorage).
 * Share links are handled upstream in ShareViewGate and never hit this gate.
 * Public v1 launch: leave both env vars unset so this gate stays inactive.
 */
export default function SitePreviewGate({ children }) {
  const previewOn = isPreviewModeEnabled();
  const validPasswords = getValidPasswords();
  const [unlocked, setUnlockedState] = useState(() =>
    !previewOn || validPasswords.length === 0 ? true : isUnlocked()
  );
  const [password, setPassword] = useState('');
  const [showError, setShowError] = useState(false);

  const locked = previewOn && validPasswords.length > 0 && !unlocked;

  useEffect(() => {
    if (!previewOn || validPasswords.length === 0) {
      setUnlockedState(true);
      return;
    }
    setUnlockedState(isUnlocked());
  }, [previewOn, validPasswords.length]);

  useEffect(() => {
    if (!locked) return undefined;
    let meta = document.querySelector('meta[name="robots"][data-rentalyzer-preview]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'robots');
      meta.setAttribute('content', 'noindex,nofollow');
      meta.setAttribute('data-rentalyzer-preview', '1');
      document.head.appendChild(meta);
    }
    return () => {
      const m = document.querySelector('meta[name="robots"][data-rentalyzer-preview]');
      if (m) m.remove();
    };
  }, [locked]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      const entered = password.trim();
      if (validPasswords.includes(entered)) {
        setUnlocked();
        setUnlockedState(true);
        setShowError(false);
        setPassword('');
      } else {
        setShowError(true);
      }
    },
    [password, validPasswords]
  );

  if (!previewOn || validPasswords.length === 0) {
    if (previewOn && validPasswords.length === 0) {
      console.warn(
        '[Rentalyzer] VITE_SITE_PREVIEW_MODE is set but VITE_PREVIEW_ACCESS_PASSWORD is empty; preview gate is disabled.'
      );
    }
    return children;
  }

  if (unlocked) {
    return children;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-slate-50 to-slate-100 px-4 py-12">
      <div className="w-full max-w-lg rounded-2xl bg-white p-8 shadow-lg border border-slate-200/80">
        <div className="text-center mb-6">
          <p className="text-sm font-medium uppercase tracking-wide text-amber-700 mb-2">
            Chantier en cours
          </p>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Rentalyzer s’installe sur le terrain
          </h1>
          <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
            Nous préparons la version publique de Rentalyzer — l’outil qui aide à analyser et
            présenter vos immeubles à revenus. Le site que vous voyez est encore en rodage.
          </p>
          <p className="mt-4 text-slate-500 text-sm italic">
            Les briques sont au bon endroit ; on finit juste de aligner les portes-fenêtres.
          </p>
        </div>

        <div className="rounded-lg bg-slate-50 border border-slate-100 p-4 mb-6">
          <p className="text-sm text-slate-700 text-center">
            L’accès à la démo privée est réservé aux personnes invitées. Si vous avez reçu un mot
            de passe, saisissez-le ci-dessous pour continuer vers la connexion Rentalyzer.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="preview-access-password" className="block text-sm font-medium text-slate-700 mb-1">
              Mot de passe d’accès
            </label>
            <input
              id="preview-access-password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
                setShowError(false);
              }}
              autoComplete="off"
              className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              placeholder="••••••••"
            />
          </div>
          {showError && (
            <p className="text-sm text-red-600" role="alert">
              Ce mot de passe ne correspond pas. Vérifiez avec la personne qui vous a invité.
            </p>
          )}
          <button
            type="submit"
            className="w-full py-2.5 text-white bg-blue-600 rounded-lg font-medium hover:bg-blue-700 transition-colors"
          >
            Entrer
          </button>
        </form>

        <p className="mt-6 text-center text-xs text-slate-400">
          Pas encore invité ? Revenez bientôt — ou écrivez-nous si vous pensez devoir avoir accès.
        </p>
      </div>
    </div>
  );
}
